<template>
  <modal-vue @close="close" :modalActive="modalStateProp">
    <template #header>
      <p class="font-bold text-start">Filtrar Políticas</p>
    </template>

    <div class="flex flex-col gap-4">
      <v-autocomplete
        ref="companyCombobox"
        v-model="selectedCompanies"
        :items="listCompanies"
        item-title="title"
        item-value="value"
        label="Selecione as Empresas"
        multiple
        chips
        clearable
        deletable-chips
        variant="outlined"
        class="w-full"
        v-model:search-input="searchCompany"
      />

      <v-select
        v-if="showStatusFilter"
        v-model="selectedStatus"
        :items="statusOptions"
        label="Status da Política"
        item-title="label"
        item-value="value"
        variant="outlined"
        clearable
        class="w-full"
      />

      <v-select
        v-if="showAuthorizationStatusFilter"
        v-model="selectedAuthorizationStatus"
        :items="authorizationOptions"
        label="Status de Autorização"
        item-title="label"
        item-value="value"
        variant="outlined"
        clearable
        class="w-full"
      />
    </div>

    <template #footer>
      <div class="flex flex-row gap-2">
        <div class="w-full order-2 md:order-1">
          <OutlineButton @click="close" text="Cancelar" width="180px" />
        </div>
        <div class="w-[180px] order-1 md:order-2">
          <GhostButton
            :loading="loading"
            @click="clearFilters"
            text="Limpar filtros"
            width="180px"
          />
        </div>
        <div class="w-[180px] order-1 md:order-2">
          <FilledButton
            :disabled="!isFilterValid"
            :loading="loading"
            @click="applyFilters"
            text="Aplicar Filtros"
            width="180px"
          />
        </div>
      </div>
    </template>
  </modal-vue>
</template>

<script setup lang="ts">
import { ref, computed, watchEffect } from 'vue'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import GhostButton from '@/components/designSystem/Buttons/GhostButton/index.vue'
import { useToast } from 'vue-toastification'
import { storeToRefs } from 'pinia'
import { useCompanyStore } from '@/pinia-store/useCompanyStore'

interface AppliedFilters {
  companies: string[]
  isActive: 'active' | 'inactive' | ''
  authorizationStatus?: 'FULLY_AUTHORIZED' | 'PARTIALLY_AUTHORIZED' | ''
}

const props = defineProps<{
  modalStateProp: boolean
  showStatusFilter?: boolean
  showAuthorizationStatusFilter?: boolean
  appliedFilters?: AppliedFilters
}>()

const emit = defineEmits(['close', 'apply-filters'])

const { listCompanies } = storeToRefs(useCompanyStore())
const toast = useToast()

const showStatusFilter = props.showStatusFilter ?? true
const showAuthorizationStatusFilter = props.showAuthorizationStatusFilter ?? true

const loading = ref(false)
const selectedCompanies = ref<string[]>([])
const searchCompany = ref('')
const selectedStatus = ref<'active' | 'inactive' | ''>('')
const selectedAuthorizationStatus = ref<'FULLY_AUTHORIZED' | 'PARTIALLY_AUTHORIZED' | ''>('')

const statusOptions = [
  { label: 'Todas', value: '' },
  { label: 'Ativas', value: 'active' },
  { label: 'Inativas', value: 'inactive' }
]

const authorizationOptions = [
  { label: 'Todos', value: '' },
  { label: 'Totalmente Autorizado', value: 'FULLY_AUTHORIZED' },
  { label: 'Parcialmente Autorizado', value: 'PARTIALLY_AUTHORIZED' }
]

const isFilterValid = computed(() => {
  const hasCompany = selectedCompanies.value.length > 0
  const hasStatus = showStatusFilter && selectedStatus.value !== ''
  const hasAuthorization = showAuthorizationStatusFilter && selectedAuthorizationStatus.value !== ''
  return hasCompany || hasStatus || hasAuthorization
})

const applyFilters = async () => {
  if (
    selectedCompanies.value.length === 0 &&
    selectedStatus.value === '' &&
    selectedAuthorizationStatus.value === ''
  ) {
    clearFilters()
    return
  }

  try {
    loading.value = true
    emit('apply-filters', {
      companies: selectedCompanies.value,
      ...(showStatusFilter && { isActive: selectedStatus.value }),
      ...(showAuthorizationStatusFilter && {
        authorizationStatus: selectedAuthorizationStatus.value
      })
    })
    emit('close')
  } catch (error) {
    console.error(error)
    toast.error('Erro ao aplicar filtros. Tente novamente.')
  } finally {
    loading.value = false
  }
}

const clearFilters = () => {
  try {
    loading.value = true
    selectedCompanies.value = []
    selectedStatus.value = ''
    selectedAuthorizationStatus.value = ''
    searchCompany.value = ''

    emit('apply-filters', {
      companies: [],
      ...(showStatusFilter && { isActive: '' }),
      ...(showAuthorizationStatusFilter && { authorizationStatus: '' })
    })
    emit('close')
  } catch (error) {
    console.error(error)
    toast.error('Erro ao limpar filtros. Tente novamente.')
  } finally {
    loading.value = false
  }
}

const close = () => {
  emit('close')
}

watchEffect(() => {
  if (props.modalStateProp) {
    if (props.appliedFilters) {
      selectedCompanies.value = props.appliedFilters.companies || []
      selectedStatus.value = props.appliedFilters.isActive || ''
      selectedAuthorizationStatus.value = props.appliedFilters.authorizationStatus || ''
    }
    searchCompany.value = ''
  }
})
</script>
